import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    InputAdornment,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
    makeStyles,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { Alert, Autocomplete } from '@material-ui/lab';
import AddIcon from '@material-ui/icons/Add';
import EventNoteOutlinedIcon from '@material-ui/icons/EventNoteOutlined';
import SearchIcon from '@material-ui/icons/Search';
import { format, parseISO } from 'date-fns';
import TopNav from '../common/TopNav';
import Container from '../Elements/Container';
import { getNodes } from '../../services/hierarchy';
import {
    createGestion,
    createGestionActivity,
    getBills,
    getGestionDetail,
    getGestiones,
    getProveedoresLector,
    patchGestion,
} from '../../services/billing-v2';

const SERVICE_OPTIONS = [
    { value: '', label: 'Tipo de servicio' },
    { value: 'electricidad', label: 'Electricidad' },
    { value: 'agua', label: 'Agua' },
    { value: 'gas', label: 'Gas' },
];

const STATUS_OPTIONS = [
    { value: 'abierta', label: 'Abierta' },
    { value: 'en_curso', label: 'En curso' },
    { value: 'pendiente_respuesta', label: 'Pendiente' },
    { value: 'cerrada', label: 'Cerrada' },
    { value: 'cancelada', label: 'Cancelada' },
];

const INITIAL_FILTERS = {
    search: '',
    jerarquia_id: '',
    proveedor_id: '',
    tipo_servicio: '',
    fecha_desde: '',
    fecha_hasta: '',
};

const INITIAL_CREATE_FORM = {
    proveedor_id: '',
    jerarquia_id: '',
    tipo_servicio: '',
    descripcion: '',
    invoiceNumber: '',
    selectedInvoiceId: '',
};

const useStyles = makeStyles(theme => ({
    root: {
        padding: theme.spacing(3),
        background: `linear-gradient(180deg, ${theme.palette.surface.panel} 0%, ${theme.palette.background.default} 100%)`,
        minHeight: 'calc(100vh - 112px)',
    },
    hero: {
        marginBottom: theme.spacing(2),
        padding: theme.spacing(2.5),
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        border: `1px solid ${theme.palette.border.main}`,
        background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.surface.soft} 100%)`,
        boxShadow: theme.palette.shadow.card,
    },
    heroRow: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        alignItems: 'flex-start',
        '@media (max-width: 960px)': {
            flexDirection: 'column',
        },
    },
    titleWrap: {
        display: 'flex',
        gap: theme.spacing(1.5),
        alignItems: 'flex-start',
    },
    titleIcon: {
        width: 52,
        height: 52,
        borderRadius: 14,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.surface.accentSoft,
    },
    pageTitle: {
        fontWeight: 700,
        color: theme.palette.grey.main,
    },
    pageSubtitle: {
        color: theme.palette.text.secondary,
        maxWidth: 640,
    },
    headerActions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
    },
    primaryButton: {
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        padding: theme.spacing(0.6, 1.75),
        textTransform: 'none',
        fontWeight: 700,
        fontSize: 13,
        color: theme.palette.common.white,
        background: `linear-gradient(180deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
        boxShadow: theme.palette.shadow.accent,
        '&:hover': {
            background: `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        },
    },
    secondaryButton: {
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        padding: theme.spacing(1, 2),
        textTransform: 'none',
        fontWeight: 700,
        backgroundColor: theme.palette.surface.alt,
        border: `1px solid ${theme.palette.border.button}`,
        color: theme.palette.text.secondary,
        '&:hover': {
            backgroundColor: theme.palette.surface.neutral,
        },
    },
    filtersCard: {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        border: `1px solid ${theme.palette.border.main}`,
        boxShadow: theme.palette.shadow.search,
    },
    filterField: {
        width: '100%',
        '& .MuiOutlinedInput-root': {
            borderRadius: theme.borderRadiusBoxes.borderRadius,
            backgroundColor: theme.palette.surface.popover,
        },
        '& .MuiInputLabel-outlined': {
            fontSize: 13,
        },
    },
    listCard: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        border: `1px solid ${theme.palette.border.soft}`,
        boxShadow: theme.palette.shadow.card,
        overflow: 'hidden',
    },
    detailCard: {
        height: '100%',
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        border: `1px solid ${theme.palette.border.soft}`,
        boxShadow: theme.palette.shadow.card,
        padding: theme.spacing(2.5),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    cardHeader: {
        padding: theme.spacing(1.5, 2),
        backgroundColor: theme.palette.surface.soft,
        borderBottom: `1px solid ${theme.palette.border.soft}`,
    },
    sectionTitle: {
        fontWeight: 700,
        color: theme.palette.grey.main,
    },
    sectionCaption: {
        color: theme.palette.text.secondary,
    },
    tableHead: {
        backgroundColor: theme.palette.primary.main,
    },
    headCell: {
        color: theme.palette.common.white,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        borderBottom: 'none',
    },
    bodyCell: {
        color: theme.palette.text.secondary,
        borderBottom: `1px solid ${theme.palette.border.subtle}`,
        verticalAlign: 'top',
    },
    clickableRow: {
        cursor: 'pointer',
        transition: 'background-color 160ms ease',
        '&:hover': {
            backgroundColor: theme.palette.surface.selected,
        },
    },
    selectedRow: {
        backgroundColor: theme.palette.surface.selectedChild,
    },
    gestionCode: {
        fontWeight: 700,
        color: theme.palette.primary.main,
    },
    descriptionCell: {
        maxWidth: 280,
        whiteSpace: 'normal',
        lineHeight: 1.45,
    },
    truncatedText: {
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
    },
    detailHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: theme.spacing(2),
        '@media (max-width: 600px)': {
            flexDirection: 'column',
        },
    },
    detailCode: {
        fontSize: 34,
        lineHeight: 1,
        fontWeight: 700,
        color: theme.palette.grey.main,
    },
    detailActions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
    },
    statusSelect: {
        minWidth: 190,
        '& .MuiOutlinedInput-root': {
            borderRadius: theme.borderRadiusBoxes.borderRadius,
            backgroundColor: theme.palette.surface.popover,
        },
    },
    infoItem: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.4),
    },
    infoLabel: {
        fontSize: 12,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 0.3,
        color: theme.palette.text.disabled,
    },
    infoValue: {
        color: theme.palette.text.primary,
        fontWeight: 600,
    },
    descriptionPanel: {
        padding: theme.spacing(2),
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        border: `1px solid ${theme.palette.border.soft}`,
        backgroundColor: theme.palette.surface.soft,
    },
    chipsRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
    },
    invoiceChip: {
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        border: `1px solid ${theme.palette.border.button}`,
        backgroundColor: theme.palette.background.default,
    },
    timeline: {
        position: 'relative',
        paddingLeft: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 8,
            bottom: 8,
            left: 7,
            width: 2,
            backgroundColor: theme.palette.border.soft,
        },
    },
    timelineItem: {
        position: 'relative',
        padding: theme.spacing(1.5, 1.75),
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        border: `1px solid ${theme.palette.border.soft}`,
        backgroundColor: theme.palette.background.default,
        boxShadow: theme.palette.shadow.card,
        '&::before': {
            content: '""',
            position: 'absolute',
            left: -16,
            top: 18,
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: theme.palette.primary.main,
            border: `2px solid ${theme.palette.background.default}`,
            boxShadow: `0 0 0 2px ${theme.palette.surface.accentSoft}`,
        },
    },
    timelineDate: {
        fontWeight: 700,
        color: theme.palette.grey.main,
        marginBottom: theme.spacing(0.5),
    },
    timelineUser: {
        color: theme.palette.text.disabled,
        fontSize: 12,
        marginBottom: theme.spacing(0.5),
    },
    timelineDetail: {
        color: theme.palette.text.secondary,
        lineHeight: 1.5,
        whiteSpace: 'pre-line',
    },
    centeredState: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 220,
        padding: theme.spacing(3),
    },
    emptyState: {
        border: `1px dashed ${theme.palette.border.main}`,
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        padding: theme.spacing(3),
        textAlign: 'center',
        backgroundColor: theme.palette.surface.soft,
    },
    dialogPaper: {
        '& .MuiDialog-paper': {
            borderRadius: theme.borderRadiusBoxes.borderRadius,
            background: `linear-gradient(180deg, ${theme.palette.surface.soft} 0%, ${theme.palette.background.default} 100%)`,
            minHeight: 580,
        },
    },
    dialogContent: {
        paddingTop: theme.spacing(1),
    },
    dialogHint: {
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(2),
    },
    invoiceFieldWrapper: {
        position: 'relative',
    },
    invoiceDropdown: {
        position: 'absolute',
        top: 'calc(100% + 2px)',
        left: 0,
        right: 0,
        zIndex: 1400,
        maxHeight: 240,
        overflowY: 'auto',
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        border: `1px solid ${theme.palette.border.soft}`,
        boxShadow: theme.palette.shadow.card,
        backgroundColor: theme.palette.background.default,
    },
    searchResultsBox: {
        marginTop: theme.spacing(1.5),
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        border: `1px solid ${theme.palette.border.soft}`,
        backgroundColor: theme.palette.background.default,
        maxHeight: 240,
        overflowY: 'auto',
    },
    searchResultRow: {
        padding: theme.spacing(1.25, 1.5),
        cursor: 'pointer',
        borderBottom: `1px solid ${theme.palette.border.subtle}`,
        '&:last-child': {
            borderBottom: 'none',
        },
        '&:hover': {
            backgroundColor: theme.palette.surface.selected,
        },
    },
    searchResultSelected: {
        backgroundColor: theme.palette.surface.selectedChild,
    },
    searchResultTitle: {
        fontWeight: 700,
        color: theme.palette.grey.main,
    },
    searchResultMeta: {
        color: theme.palette.text.secondary,
        fontSize: 13,
    },
    dialogActions: {
        padding: theme.spacing(0, 3, 3),
    },
    inlineAlert: {
        marginBottom: theme.spacing(2),
    },
}));



const normalizeBill = bill => ({
    id: bill?.id,
    tipo_servicio: bill?.tipo_servicio || bill?.tipo || '',
    numero_factura: bill?.numero_factura || bill?.nro_factura || `#${bill?.id}`,
    proveedor: bill?.proveedor || bill?.proveedor_nombre || bill?.provider || '',
    jerarquia_nombre: bill?.jerarquia_nombre || bill?.location_name || bill?.sitio || '',
});

const formatDate = value => {
    if (!value) {
        return '-';
    }

    try {
        return format(parseISO(value), 'dd/MM/yyyy');
    }
    catch (error) {
        return value;
    }
};

const formatDateTime = value => {
    if (!value) {
        return '-';
    }

    try {
        return format(parseISO(value), 'dd/MM/yyyy HH:mm');
    }
    catch (error) {
        return value;
    }
};

const getStatusMeta = (theme, status) => {
    const option = STATUS_OPTIONS.find(item => item.value === status);
    const paletteByStatus = {
        abierta: { backgroundColor: theme.palette.warning.light, color: theme.palette.warning.dark },
        en_curso: { backgroundColor: theme.palette.surface.selectedChild, color: theme.palette.primary.main },
        pendiente_respuesta: { backgroundColor: '#fff4cc', color: '#9a6700' },
        cerrada: { backgroundColor: theme.palette.status.successSoft, color: theme.palette.status.success },
        cancelada: { backgroundColor: theme.palette.error.light, color: theme.palette.error.main },
    };

    return {
        label: option?.label || status || 'Sin estado',
        style: paletteByStatus[status] || {
            backgroundColor: theme.palette.surface.neutral,
            color: theme.palette.text.secondary,
        },
    };
};

const getGestionInvoiceSummary = gestion => {
    const invoices = gestion?.facturas || [];

    if (!invoices.length) {
        return '-';
    }

    if (invoices.length === 1) {
        return invoices[0]?.numero_factura || `#${invoices[0]?.id}`;
    }

    const firstInvoice = invoices[0]?.numero_factura || `#${invoices[0]?.id}`;
    return `${firstInvoice} +${invoices.length - 1}`;
};

function BillingManagement() {
    const classes = useStyles();
    const theme = useTheme();
    const [filters, setFilters] = useState(INITIAL_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS);
    const [providers, setProviders] = useState([]);
    const [createProviders, setCreateProviders] = useState([]);
    const [createProveedoresLoading, setCreateProveedoresLoading] = useState(false);
    const [locations, setLocations] = useState([]);
    const [gestiones, setGestiones] = useState([]);
    const [loadingList, setLoadingList] = useState(true);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [selectedGestionId, setSelectedGestionId] = useState(null);
    const [selectedGestion, setSelectedGestion] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalGestiones, setTotalGestiones] = useState(0);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [activityDialogOpen, setActivityDialogOpen] = useState(false);
    const [createForm, setCreateForm] = useState(INITIAL_CREATE_FORM);
    const [invoiceResults, setInvoiceResults] = useState([]);
    const [invoiceSearchLoading, setInvoiceSearchLoading] = useState(false);
    const [submittingGestion, setSubmittingGestion] = useState(false);
    const [submittingActivity, setSubmittingActivity] = useState(false);
    const [newActivityDetail, setNewActivityDetail] = useState('');
    const [feedback, setFeedback] = useState({ open: false, type: 'success', message: '' });
    const [pageError, setPageError] = useState('');

    const selectedInvoice = useMemo(
        () => invoiceResults.find(item => String(item.id) === String(createForm.selectedInvoiceId)) || null,
        [createForm.selectedInvoiceId, invoiceResults],
    );

    const showFeedback = (message, type = 'success') => {
        setFeedback({
            open: false,
            message,
            type,
        });
    };

    const loadGestionDetail = useCallback(async gestionId => {
        if (!gestionId) {
            setSelectedGestionId(null);
            setSelectedGestion(null);
            return;
        }

        setLoadingDetail(true);
        setSelectedGestionId(gestionId);

        try {
            const response = await getGestionDetail(gestionId);
            const gestionData = response?.data || null;

            setSelectedGestion({
                ...gestionData,
                actividades: Array.isArray(gestionData?.actividades)
                    ? [...gestionData.actividades].sort((firstItem, secondItem) => (
                        new Date(firstItem?.fecha || 0).getTime() - new Date(secondItem?.fecha || 0).getTime()
                    ))
                    : [],
            });
        }
        catch (error) {
            setSelectedGestion(null);
            setPageError('No pudimos cargar el detalle de la gestión seleccionada.');
        }
        finally {
            setLoadingDetail(false);
        }
    }, []);

    const loadGestiones = useCallback(async () => {
        setLoadingList(true);
        setPageError('');

        try {
            const response = await getGestiones({
                ...appliedFilters,
                page: page + 1,
                page_size: rowsPerPage,
            });

            const responseData = response?.data || {};
            const results = Array.isArray(responseData?.results) ? responseData.results : [];
            setGestiones(results);
            setTotalGestiones(responseData?.count || 0);

            if (!results.length) {
                setSelectedGestionId(null);
                setSelectedGestion(null);
                return;
            }

            const stillSelected = results.some(item => String(item?.id) === String(selectedGestionId));
            const nextSelectedId = stillSelected ? selectedGestionId : results[0]?.id;
            await loadGestionDetail(nextSelectedId);
        }
        catch (error) {
            setGestiones([]);
            setTotalGestiones(0);
            setSelectedGestionId(null);
            setSelectedGestion(null);
            setPageError('No pudimos obtener el listado de gestiones.');
        }
        finally {
            setLoadingList(false);
        }
    }, [appliedFilters, loadGestionDetail, page, rowsPerPage, selectedGestionId]);

    useEffect(() => {
        const loadMetadata = async () => {
            try {
                const [providersResponse, nodesResponse] = await Promise.all([
                    getProveedoresLector(),
                    getNodes(),
                ]);

                const providerItems = Array.isArray(providersResponse?.data?.results)
                    ? providersResponse.data.results.map(p => ({ id: String(p.id), name: p.nombre })).filter(item => item.id && item.name)
                    : [];
                const nodes = Array.isArray(nodesResponse?.data)
                    ? nodesResponse.data
                    : nodesResponse?.data?.results || [];
                const locationItems = nodes
                    .filter(n => n.level_nombre === 'localización')
                    .map(n => ({ id: String(n.id), name: n.nombre }))
                    .filter(item => item.id && item.name);

                setProviders(providerItems);
                setLocations(locationItems);
            }
            catch (error) {
                setPageError('No pudimos cargar los filtros auxiliares de proveedores y localizaciones.');
            }
        };

        loadMetadata();
    }, []);

    useEffect(() => {
        loadGestiones();
    }, [loadGestiones]);

    useEffect(() => {
        if (!createDialogOpen) return;
        setCreateProveedoresLoading(true);
        getProveedoresLector({ tipo_servicio: createForm.tipo_servicio || '' })
            .then(r => setCreateProviders(Array.isArray(r?.data?.results) ? r.data.results.map(p => ({ id: String(p.id), name: p.nombre })) : []))
            .catch(() => setCreateProviders([]))
            .finally(() => setCreateProveedoresLoading(false));
    }, [createDialogOpen, createForm.tipo_servicio]);

    const handleFilterChange = event => {
        const { name, value } = event.target;
        setFilters(previousState => ({
            ...previousState,
            [name]: value,
        }));
    };

    const handleApplyFilters = () => {
        setPage(0);
        setAppliedFilters(filters);
    };

    const handleClearFilters = () => {
        setFilters(INITIAL_FILTERS);
        setAppliedFilters(INITIAL_FILTERS);
        setPage(0);
    };

    const handleOpenCreateDialog = () => {
        setCreateForm(INITIAL_CREATE_FORM);
        setInvoiceResults([]);
        setCreateDialogOpen(true);
    };

    const handleCloseCreateDialog = () => {
        setCreateDialogOpen(false);
        setCreateForm(INITIAL_CREATE_FORM);
        setInvoiceResults([]);
    };

    const handleCreateFormChange = event => {
        const { name, value } = event.target;
        setCreateForm(previousState => ({
            ...previousState,
            [name]: value,
            ...(name === 'tipo_servicio' ? { selectedInvoiceId: '', proveedor_id: '' } : {}),
        }));

        if (['proveedor_id', 'tipo_servicio', 'jerarquia_id'].includes(name)) {
            setInvoiceResults([]);
        }
    };

    const handleSearchInvoices = async () => {
        if (!createForm.tipo_servicio && !createForm.proveedor_id) return;

        setInvoiceSearchLoading(true);

        try {
            const response = await getBills({
                tipo: createForm.tipo_servicio,
                tipo_servicio: createForm.tipo_servicio,
                ...(createForm.invoiceNumber.trim() ? { numero_factura: createForm.invoiceNumber.trim() } : {}),
                proveedor_id: createForm.proveedor_id,
                jerarquia_id: createForm.jerarquia_id,
                page: 1,
                page_size: 15,
            });

            const results = Array.isArray(response?.data?.results)
                ? response.data.results.map(normalizeBill)
                : [];
            setInvoiceResults(results);
        }
        catch (error) {
            setInvoiceResults([]);
        }
        finally {
            setInvoiceSearchLoading(false);
        }
    };

    const handleSelectInvoice = invoice => {
        setCreateForm(previousState => ({
            ...previousState,
            invoiceNumber: invoice.numero_factura || `#${invoice.id}`,
            selectedInvoiceId: String(invoice.id),
        }));
    };

    // Debounced auto-search while typing
    useEffect(() => {
        if (!createDialogOpen) return;
        const query = createForm.invoiceNumber.trim();
        if (query.length < 2) return;

        const timer = setTimeout(() => {
            handleSearchInvoices();
        }, 500);

        return () => clearTimeout(timer);
    }, [createForm.invoiceNumber, createForm.proveedor_id, createForm.tipo_servicio, createForm.jerarquia_id, createDialogOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCreateGestion = async () => {
        if (!createForm.proveedor_id || !createForm.tipo_servicio) {
            return;
        }

        if (!createForm.descripcion.trim()) {
            showFeedback('La descripción es obligatoria.', 'warning');
            return;
        }

        setSubmittingGestion(true);

        try {
            const payload = {
                proveedor_id: Number(createForm.proveedor_id),
                descripcion: createForm.descripcion.trim(),
                ...(createForm.tipo_servicio ? { tipo_servicio: createForm.tipo_servicio } : {}),
                ...(createForm.jerarquia_id ? { jerarquia_id: Number(createForm.jerarquia_id) } : {}),
                ...(selectedInvoice ? {
                    facturas: [{
                        tipo: selectedInvoice?.tipo_servicio || createForm.tipo_servicio,
                        id: selectedInvoice.id,
                    }],
                } : {}),
            };

            const response = await createGestion(payload);
            const createdGestionId = response?.data?.id;

            handleCloseCreateDialog();
            setPage(0);
            await loadGestiones();

            if (createdGestionId) {
                await loadGestionDetail(createdGestionId);
            }

            showFeedback('La gestión se creó correctamente.');
        }
        catch (error) {
            showFeedback('No pudimos crear la gestión. Revisá los datos e intentá nuevamente.', 'error');
        }
        finally {
            setSubmittingGestion(false);
        }
    };

    const handleStatusChange = async event => {
        const nextStatus = event.target.value;

        if (!selectedGestionId || !selectedGestion || nextStatus === selectedGestion.estado) {
            return;
        }

        try {
            await patchGestion(selectedGestionId, { estado: nextStatus });
            await Promise.all([loadGestiones(), loadGestionDetail(selectedGestionId)]);
            showFeedback('Estado de la gestión actualizado.');
        }
        catch (error) {
            showFeedback('No pudimos actualizar el estado de la gestión.', 'error');
        }
    };

    const handleCreateActivity = async () => {
        if (!selectedGestionId || !newActivityDetail.trim()) {
            showFeedback('Completá el detalle de la actividad antes de guardarla.', 'warning');
            return;
        }

        setSubmittingActivity(true);

        try {
            await createGestionActivity(selectedGestionId, {
                detalle: newActivityDetail.trim(),
            });

            setActivityDialogOpen(false);
            setNewActivityDetail('');
            await Promise.all([loadGestiones(), loadGestionDetail(selectedGestionId)]);
            showFeedback('La actividad se agregó correctamente.');
        }
        catch (error) {
            showFeedback('No pudimos agregar la actividad.', 'error');
        }
        finally {
            setSubmittingActivity(false);
        }
    };

    return (
        <>
            <TopNav titulo='Facturación' subtitulo='| Gestiones'>
                <Box style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant='contained'
                        disableElevation
                        className={classes.primaryButton}
                        startIcon={<AddIcon />}
                        onClick={handleOpenCreateDialog}
                    >
                        Nueva gestión
                    </Button>
                </Box>
            </TopNav>
            <Container>
                <Box className={classes.root}>
                    <Box className={classes.titleWrap} style={{ marginBottom: 30 }}>
                        <Box className={classes.titleIcon}>
                            <EventNoteOutlinedIcon />
                        </Box>
                        <Box>
                            <Typography variant='h6' className={classes.pageTitle}>
                                Gestiones con proveedores
                            </Typography>
                            <Typography className={classes.pageSubtitle}>
                                Registrá reclamos, seguimientos y resultados para tener trazabilidad completa de cada interacción.
                            </Typography>
                        </Box>
                    </Box>

                    <Paper className={classes.filtersCard}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    className={classes.filterField}
                                    variant='outlined'
                                    label='Buscar gestión'
                                    name='search'
                                    value={filters.search}
                                    onChange={handleFilterChange}
                                    onKeyPress={event => {
                                        if (event.key === 'Enter') {
                                            handleApplyFilters();
                                        }
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position='end'>
                                                <SearchIcon color='action' />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <TextField select className={classes.filterField} variant='outlined' label='Localización' name='jerarquia_id' value={filters.jerarquia_id} onChange={handleFilterChange}>
                                    <MenuItem value=''>Todas</MenuItem>
                                    {locations.map(location => (
                                        <MenuItem key={location.id} value={location.id}>
                                            {location.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <TextField select className={classes.filterField} variant='outlined' label='Proveedor' name='proveedor_id' value={filters.proveedor_id} onChange={handleFilterChange}>
                                    <MenuItem value=''>Todos</MenuItem>
                                    {providers.map(provider => (
                                        <MenuItem key={provider.id} value={provider.id}>
                                            {provider.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <TextField select className={classes.filterField} variant='outlined' label='Servicio' name='tipo_servicio' value={filters.tipo_servicio} onChange={handleFilterChange}>
                                    {SERVICE_OPTIONS.map(option => (
                                        <MenuItem key={option.value || 'all'} value={option.value}>
                                            {option.value ? option.label : 'Todos'}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={1}>
                                <TextField type='date' className={classes.filterField} variant='outlined' label='Desde' name='fecha_desde' value={filters.fecha_desde} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={1}>
                                <TextField type='date' className={classes.filterField} variant='outlined' label='Hasta' name='fecha_hasta' value={filters.fecha_hasta} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} />
                            </Grid>
                            <Grid item xs={12}>
                                <Box className={classes.headerActions}>
                                    <Button variant='contained' disableElevation className={classes.primaryButton} onClick={handleApplyFilters}>
                                        Aplicar filtros
                                    </Button>
                                    <Button className={classes.secondaryButton} onClick={handleClearFilters}>
                                        Limpiar
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>

                    {pageError && (
                        <Alert severity='warning' className={classes.inlineAlert}>
                            {pageError}
                        </Alert>
                    )}

                    <Grid container spacing={2}>
                        <Grid item xs={12} lg={7}>
                            <Paper className={classes.listCard}>
                                <Box className={classes.cardHeader}>
                                    <Typography className={classes.sectionTitle}>Gestiones registradas</Typography>
                                </Box>

                                {loadingList ? (
                                    <Box className={classes.centeredState}>
                                        <CircularProgress size={28} />
                                    </Box>
                                ) : gestiones.length === 0 ? (
                                    <Box className={classes.centeredState}>
                                        <Box className={classes.emptyState}>
                                            <Typography className={classes.sectionTitle}>No hay gestiones para mostrar</Typography>
                                            <Typography className={classes.sectionCaption}>
                                                Probá ajustando los filtros o creando una nueva gestión.
                                            </Typography>
                                        </Box>
                                    </Box>
                                ) : (
                                    <>
                                        <TableContainer>
                                            <Table size='small'>
                                                <TableHead className={classes.tableHead}>
                                                    <TableRow>
                                                        <TableCell className={classes.headCell}>ID</TableCell>
                                                        <TableCell className={classes.headCell}>Inicio</TableCell>
                                                        <TableCell className={classes.headCell}>Proveedor</TableCell>
                                                        <TableCell className={classes.headCell}>N° factura</TableCell>
                                                        <TableCell className={classes.headCell}>Responsable</TableCell>
                                                        <TableCell className={classes.headCell}>Descripción</TableCell>
                                                        <TableCell className={classes.headCell}>Estado</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {gestiones.map(gestion => {
                                                        const statusMeta = getStatusMeta(theme, gestion?.estado);
                                                        const isSelected = String(gestion?.id) === String(selectedGestionId);

                                                        return (
                                                            <TableRow key={gestion.id} hover onClick={() => loadGestionDetail(gestion.id)} className={`${classes.clickableRow} ${isSelected ? classes.selectedRow : ''}`}>
                                                                <TableCell className={classes.bodyCell}>
                                                                    <span className={classes.gestionCode}>{String(gestion.id).padStart(5, '0')}</span>
                                                                </TableCell>
                                                                <TableCell className={classes.bodyCell}>{formatDate(gestion.fecha_inicio)}</TableCell>
                                                                <TableCell className={classes.bodyCell}>{gestion.proveedor_nombre || '-'}</TableCell>
                                                                <TableCell className={classes.bodyCell}>{gestion.facturas_count ? `${gestion.facturas_count} vinculada${gestion.facturas_count === 1 ? '' : 's'}` : '-'}</TableCell>
                                                                <TableCell className={classes.bodyCell}>{gestion.responsable_email || '-'}</TableCell>
                                                                <TableCell className={`${classes.bodyCell} ${classes.descriptionCell}`}>
                                                                    <span className={classes.truncatedText}>{gestion.descripcion || '-'}</span>
                                                                </TableCell>
                                                                <TableCell className={classes.bodyCell}>
                                                                    <Chip size='small' label={statusMeta.label} style={{ ...statusMeta.style, borderRadius: 4, fontWeight: 700 }} />
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>

                                        <TablePagination
                                            component='div'
                                            rowsPerPageOptions={[10, 20, 50]}
                                            count={totalGestiones}
                                            rowsPerPage={rowsPerPage}
                                            page={page}
                                            onChangePage={(event, nextPage) => setPage(nextPage)}
                                            onChangeRowsPerPage={event => {
                                                setRowsPerPage(Number(event.target.value));
                                                setPage(0);
                                            }}
                                            labelRowsPerPage='Filas por página'
                                        />
                                    </>
                                )}
                            </Paper>
                        </Grid>

                        <Grid item xs={12} lg={5}>
                            <Paper className={classes.detailCard}>
                                {loadingDetail ? (
                                    <Box className={classes.centeredState}>
                                        <CircularProgress size={28} />
                                    </Box>
                                ) : !selectedGestion ? (
                                    <Box className={classes.centeredState}>
                                        <Box className={classes.emptyState}>
                                            <Typography className={classes.sectionTitle}>Seleccioná una gestión</Typography>
                                            <Typography className={classes.sectionCaption}>
                                                En esta columna vas a ver el detalle completo y el historial de actividades.
                                            </Typography>
                                        </Box>
                                    </Box>
                                ) : (
                                    <>
                                        <Box className={classes.detailHeader}>
                                            <Box>
                                                <Typography className={classes.detailCode}>{String(selectedGestion.id).padStart(5, '0')}</Typography>
                                                <Typography className={classes.sectionCaption}>Gestión iniciada el {formatDate(selectedGestion.fecha_inicio)}</Typography>
                                            </Box>

                                            <Box className={classes.detailActions}>
                                                <FormControl variant='outlined' size='small' className={classes.statusSelect}>
                                                    <Select value={selectedGestion.estado || ''} onChange={handleStatusChange}>
                                                        {STATUS_OPTIONS.map(option => (
                                                            <MenuItem key={option.value} value={option.value}>
                                                                {option.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                                <Button className={classes.primaryButton} startIcon={<AddIcon />} onClick={() => setActivityDialogOpen(true)}>
                                                    Agregar actividad
                                                </Button>
                                            </Box>
                                        </Box>

                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <Box className={classes.infoItem}>
                                                    <Typography className={classes.infoLabel}>Proveedor</Typography>
                                                    <Typography className={classes.infoValue}>{selectedGestion.proveedor_nombre || '-'}</Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Box className={classes.infoItem}>
                                                    <Typography className={classes.infoLabel}>Responsable</Typography>
                                                    <Typography className={classes.infoValue}>{selectedGestion.responsable_email || '-'}</Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Box className={classes.infoItem}>
                                                    <Typography className={classes.infoLabel}>Localización</Typography>
                                                    <Typography className={classes.infoValue}>{selectedGestion.jerarquia_nombre || '-'}</Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Box className={classes.infoItem}>
                                                    <Typography className={classes.infoLabel}>Servicio</Typography>
                                                    <Typography className={classes.infoValue}>
                                                        {SERVICE_OPTIONS.find(option => option.value === selectedGestion.tipo_servicio)?.label || '-'}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Box className={classes.infoItem}>
                                                    <Typography className={classes.infoLabel}>Facturas asociadas</Typography>
                                                    <Typography className={classes.infoValue}>{getGestionInvoiceSummary(selectedGestion)}</Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>

                                        <Box className={classes.descriptionPanel}>
                                            <Typography className={classes.sectionTitle}>Descripción</Typography>
                                            <Typography className={classes.timelineDetail}>
                                                {selectedGestion.descripcion || 'Sin descripción.'}
                                            </Typography>
                                        </Box>

                                        {selectedGestion.facturas?.length > 0 && (
                                            <Box>
                                                <Typography className={classes.sectionTitle}>Facturas vinculadas</Typography>
                                                <Box className={classes.chipsRow}>
                                                    {selectedGestion.facturas.map(invoice => (
                                                        <Chip
                                                            key={invoice.link_id || invoice.id}
                                                            className={classes.invoiceChip}
                                                            label={`${invoice.numero_factura || `#${invoice.id}`} · ${invoice.proveedor || selectedGestion.proveedor_nombre || 'Proveedor'}`}
                                                        />
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}

                                        <Divider />

                                        <Box>
                                            <Typography className={classes.sectionTitle}>Lista de actividades</Typography>
                                            <Typography className={classes.sectionCaption}>
                                                Ordenadas cronológicamente para seguir el historial completo.
                                            </Typography>
                                        </Box>

                                        {selectedGestion.actividades?.length ? (
                                            <Box className={classes.timeline}>
                                                {selectedGestion.actividades.map(activity => (
                                                    <Box key={activity.id} className={classes.timelineItem}>
                                                        <Typography className={classes.timelineDate}>
                                                            {formatDateTime(activity.fecha)}
                                                        </Typography>
                                                        <Typography className={classes.timelineUser}>
                                                            {activity.usuario_email || 'Usuario del sistema'}
                                                        </Typography>
                                                        <Typography className={classes.timelineDetail}>{activity.detalle}</Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        ) : (
                                            <Box className={classes.emptyState}>
                                                <Typography className={classes.sectionTitle}>Todavía no hay actividades</Typography>
                                                <Typography className={classes.sectionCaption}>
                                                    Registrá la primera interacción con el proveedor para empezar el seguimiento.
                                                </Typography>
                                            </Box>
                                        )}
                                    </>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>
            </Container>

            <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} fullWidth maxWidth='md' className={classes.dialogPaper}>
                <DialogTitle>Nueva gestión</DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    <Typography className={classes.dialogHint}>
                        Completá los datos para registrar una nueva gestión con un proveedor. Podés vincular la gestión a una factura existente o dejarla sin factura para casos más generales.
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField required select label='Tipo de servicio' name='tipo_servicio' value={createForm.tipo_servicio} onChange={handleCreateFormChange} variant='outlined' className={classes.filterField}>
                                <MenuItem value=''>Seleccionar tipo</MenuItem>
                                {SERVICE_OPTIONS.filter(o => o.value).map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField required select label='Proveedor' name='proveedor_id' value={createForm.proveedor_id} onChange={handleCreateFormChange} variant='outlined' className={classes.filterField} disabled={createProveedoresLoading}>
                                <MenuItem value=''>Seleccionar proveedor</MenuItem>
                                {createProviders.map(provider => (
                                    <MenuItem key={provider.id} value={provider.id}>
                                        {provider.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField select label='Localización' name='jerarquia_id' value={createForm.jerarquia_id} onChange={handleCreateFormChange} variant='outlined' className={classes.filterField}>
                                <MenuItem value=''>Opcional</MenuItem>
                                {locations.map(location => (
                                    <MenuItem key={location.id} value={location.id}>
                                        {location.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                freeSolo
                                options={invoiceResults}
                                getOptionLabel={invoice => typeof invoice === 'string' ? invoice : (invoice.numero_factura || `#${invoice.id}`)}
                                getOptionSelected={(option, value) => typeof option === 'object' && typeof value === 'object' && String(option.id) === String(value.id)}
                                value={selectedInvoice}
                                inputValue={createForm.invoiceNumber}
                                onInputChange={(_, newValue, reason) => {
                                    if (reason === 'input' || reason === 'clear') {
                                        setCreateForm(prev => ({ ...prev, invoiceNumber: newValue, selectedInvoiceId: '' }));
                                        if (!newValue) setInvoiceResults([]);
                                    }
                                }}
                                onChange={(_, newValue) => {
                                    if (newValue && typeof newValue === 'object') {
                                        handleSelectInvoice(newValue);
                                    }
                                }}
                                onOpen={handleSearchInvoices}
                                filterOptions={x => x}
                                loading={invoiceSearchLoading}
                                noOptionsText='Sin resultados'
                                loadingText='Buscando...'
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        label='N° de factura'
                                        variant='outlined'
                                        className={classes.filterField}
                                        placeholder='Opcional'
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {invoiceSearchLoading && <CircularProgress size={16} />}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                renderOption={invoice => (
                                    <Box>
                                        <Typography className={classes.searchResultTitle}>
                                            {invoice.numero_factura || `#${invoice.id}`}
                                        </Typography>
                                        <Typography className={classes.searchResultMeta}>
                                            {invoice.proveedor || 'Proveedor sin dato'}{invoice.jerarquia_nombre ? ` · ${invoice.jerarquia_nombre}` : ''}
                                        </Typography>
                                    </Box>
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label='Descripción'
                                name='descripcion'
                                value={createForm.descripcion}
                                onChange={handleCreateFormChange}
                                variant='outlined'
                                className={classes.filterField}
                                multiline
                                minRows={4}
                                placeholder='Describí el motivo del reclamo o trámite y el contexto necesario.'
                            />
                        </Grid>
                    </Grid>

                </DialogContent>
                <DialogActions className={classes.dialogActions}>
                    <Button onClick={handleCloseCreateDialog} className={classes.secondaryButton}>Cancelar</Button>
                    <Button variant='contained' disableElevation className={classes.primaryButton} onClick={handleCreateGestion} disabled={submittingGestion || !createForm.proveedor_id || !createForm.tipo_servicio}>
                        {submittingGestion ? 'Guardando...' : 'Crear gestión'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={activityDialogOpen}
                onClose={() => {
                    if (!submittingActivity) {
                        setActivityDialogOpen(false);
                        setNewActivityDetail('');
                    }
                }}
                fullWidth
                maxWidth='sm'
                className={classes.dialogPaper}
            >
                <DialogTitle>Agregar actividad</DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    <Typography className={classes.dialogHint}>
                        La fecha se registra automáticamente con el momento de creación de la actividad.
                    </Typography>
                    <TextField
                        label='Detalle de la actividad'
                        value={newActivityDetail}
                        onChange={event => setNewActivityDetail(event.target.value)}
                        variant='outlined'
                        className={classes.filterField}
                        multiline
                        minRows={5}
                        placeholder='Ejemplo: llamada al proveedor, respuesta recibida, pedido de documentación, etc.'
                    />
                </DialogContent>
                <DialogActions className={classes.dialogActions}>
                    <Button
                        onClick={() => {
                            setActivityDialogOpen(false);
                            setNewActivityDetail('');
                        }}
                        className={classes.secondaryButton}
                    >
                        Cancelar
                    </Button>
                    <Button variant='contained' disableElevation className={classes.primaryButton} onClick={handleCreateActivity} disabled={submittingActivity}>
                        {submittingActivity ? 'Guardando...' : 'Guardar actividad'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={feedback.open}
                autoHideDuration={3500}
                onClose={() => setFeedback(previousState => ({ ...previousState, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    elevation={6}
                    variant='filled'
                    severity={feedback.type}
                    onClose={() => setFeedback(previousState => ({ ...previousState, open: false }))}
                >
                    {feedback.message}
                </Alert>
            </Snackbar>
        </>
    );
}

export default BillingManagement;
